import React, { useEffect, useState, useCallback } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { ProgressBar, Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import {
  FORM_SLIDE,
  EMPTY_STRING,
  authConstants,
  defaultSchedule,
  SLIDE_DIRECTION,
  SELECT_FORM_SLIDE,
} from "../../constants";
import { CloseAdminModalIcon } from "../../assets";
import ArrowLeftIcon from "../../assets/icons/property-types/arrow-left-grey.svg";
import ArrowRightIcon from "../../assets/icons/property-types/arrow-right-grey.svg";
import CustomSvg from "../../shared/components/custom-svg/CustomSvg";
import { authActions, countryAction } from "../../redux/actions";
import EmergencyContactForm from "./forms/EmergencyContactForm";
import AgentInformationForm from "./forms/AgentInformationForm";
import CompanyInformationForm from "./forms/CompanyInformationForm";
import CompanyOfficeHoursForm from "./forms/CompanyOfficeHoursForm";

const totalFormSlides = 4;

// Define the validation schema.
const validationSchema = (isRequired) =>
  yup.object().shape({
    company: yup.object().shape({
      name: yup
        .string()
        .trim()
        .required("Company name is required")
        .min(1, "Name must be at least 1 character")
        .max(100, "Name must not exceed 100 characters"),
      phoneNumber: yup.string().required("Phone number is required"),
      licenseNumber: yup.string().trim().required("Licence number is required"),
      websiteUrl: yup
        .string()
        .required("Website URL is required")
        .max(500, "URL must not exceed 500 characters")
        .test("is-valid-url", "Must be a valid URL", (value) => {
          if (!value) return false; // Required field
          const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*$/;
          return urlPattern.test(value);
        }),
      addressLine1: yup
        .string()
        .trim()
        .required("Address Line 1 is required")
        .max(200, "Address Line 1 should not exceed 200 characters"),
      addressLine2: yup
        .string()
        .trim()
        .nullable()
        .max(200, "Address Line 2 should not exceed 200 characters"),
      city: yup.string().trim().required("City is required"),
      stateAndProvince: yup
        .string()
        .trim()
        .required("State/Province is required"),
      countryId: yup.string().required("Country is required"),
    }),
    officeHours: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required("Office hour name is required"),
          isActive: yup.boolean().required("Active status is required"),
          startTime: yup.string().required("Start time is required"),
          endTime: yup.string().required("End time is required"),
          startTimeError: yup
            .string()
            .nullable()
            .oneOf([null], "Start time error must be null"),
          endTimeError: yup
            .string()
            .nullable()
            .oneOf([null], "End time error must be null"),
        })
      )
      .min(7, "Valid office hour is required")
      .test(
        "at-least-one-active",
        "At least one day must be active",
        (value) => value?.some((item) => item.isActive) // Custom test
      ),
    agent: yup.object().shape({
      firstName: isRequired
        ? yup
            .string()
            .trim()
            .required("First name is required")
            .max(100, "First name must not exceed 100 characters")
        : yup.string(),
      lastName: isRequired
        ? yup
            .string()
            .trim()
            .required("Last name is required")
            .max(100, "Last name must not exceed 100 characters")
        : yup.string(),
      phoneNumber: yup.string().required("Phone number is required"),
      email: isRequired
        ? yup
            .string()
            .required("Email is required")
            .max(100, "Email must not exceed 100 characters")
            .test(
              "is-valid-email",
              "Invalid email address",
              (value) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            )
        : yup.string(),
      languageSpokenIds: yup
        .array()
        .min(1, "At least one language is required")
        .required("Languages spoken is required"),
      preferredContactMethodId: yup
        .string()
        .required("Preferred contact method is required"),
      password: isRequired
        ? yup
            .string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(
              /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?`~])[a-zA-Z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]+$/,
              "Password must include letters, numbers, and at least one special character"
            )
        : yup.string(),
      confirmPassword: isRequired
        ? yup
            .string()
            .oneOf([yup.ref("password"), null], "Passwords must match")
            .required("Confirm password is required")
        : yup.string(),
    }),
    emergencyContactInfo: yup.object().shape({
      fullName: yup
        .string()
        .trim()
        .required("Emergency contact full name is required")
        .max(100, "Full name must not exceed 100 characters"),
      phoneNumber: yup
        .string()
        .required("Emergency contact phone number is required"),
      files: yup
        .array()
        .min(2, "At least two photo ID is required")
        .required("Photo ID is required"),
      headshotfiles: yup
        .array()
        .min(1, "At least one professional headshot is required")
        .required("Professional Headshot is required"),
    }),
    document: yup
      .array()
      .min(1, "At least one certificate is required")
      .required("Registration certificate is required"),
  });

const SignupForm = (props) => {
  const {
    userId = null,
    allCountries,
    allLanguages,
    show,
    onClose,
    closeModal,

    getAllCountries,
    getAllLanguages,
    agentRegisteration,
    spinnerActions,

    defaultValues={}
  } = props;

  const [schedule, setSchedule] = useState(() => _.cloneDeep(defaultSchedule));
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState([]);
  const [selectedPreferredContactMethod, setSelectedPreferredContactMethod] =
    useState(null);

  const [direction, setDirection] = useState(SLIDE_DIRECTION.NEXT);
  const [currentSlide, setCurrentSlide] = useState(
    FORM_SLIDE.COMPANY_INFORAMTION
  );

  const methods = useForm({
    defaultValues:defaultValues,
    mode: "onChange",
    resolver: yupResolver(validationSchema(userId ? false : true)), // Apply the yup schema here
  });
  const {
    trigger,
    setValue,
    clearErrors,
    formState: { errors },
  } = methods;

  const getProgress = () => (currentSlide / totalFormSlides) * 100;

  const handleNext = async () => {
    const isValid = await trigger(SELECT_FORM_SLIDE[currentSlide]);

    if (isValid) {
      setDirection(SLIDE_DIRECTION.NEXT);
      setTimeout(() => {
        setCurrentSlide((prev) => prev + 1);
      }, 0);
    }
  };

  const handlePrevious = () => {
    setDirection(SLIDE_DIRECTION.PREVIOUS);
    setTimeout(() => {
      setCurrentSlide((prev) => prev - 1);
    }, 0);
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const handleRegistrationSuccess = () => {
    setSchedule({ ...defaultSchedule });
    setSelectedCountry(null);
    setSelectedLanguage([]);
    setSelectedPreferredContactMethod(null);
    closeModal();
  };

  const onSubmit = (data) => {
    const { agent, company, emergencyContactInfo, document } = data;

    let formdata = new FormData();
    // Agent info
    if (userId) {
      formdata.append("agent.customerId", userId);
    }
    formdata.append("agent.firstName", agent.firstName);
    formdata.append("agent.lastName", agent.lastName);
    formdata.append("agent.phoneNumber", agent.phoneNumber);
    formdata.append("agent.email", agent.email);
    formdata.append(
      "agent.preferredContactMethod",
      selectedPreferredContactMethod.id
    );
    formdata.append("agent.password", agent.password);
    [...selectedLanguage].forEach((lang) => {
      formdata.append("agent.languageSpokenIds", lang.id);
    });

    // Agent emergency info
    formdata.append(
      "agent.emergencyContactInfo.fullName",
      emergencyContactInfo.fullName
    );
    formdata.append(
      "agent.emergencyContactInfo.phoneNumber",
      emergencyContactInfo.phoneNumber
    );

    [...emergencyContactInfo.files].forEach((img, index) => {
      formdata.append(`photoId${index + 1}`, img);
    });
    [...emergencyContactInfo.headshotfiles].forEach((img) => {
      formdata.append(img.name, img);
    });
    [...document].forEach((img) => {
      formdata.append("document", img);
    });

    // Company info
    formdata.append("company.name", company.name);
    formdata.append("company.phoneNumber", company.phoneNumber);
    formdata.append("company.licenseNumber", company.licenseNumber);
    formdata.append("company.websiteUrl", company.websiteUrl);
    formdata.append("company.addressLine1", company.addressLine1);
    formdata.append("company.addressLine2", company.addressLine2);
    formdata.append("company.countryId", selectedCountry.id);
    formdata.append("company.state", company.stateAndProvince);
    formdata.append("company.city", company.city);

    // Company office hour info
    [...schedule].forEach((s, index) => {
      formdata.append(`company.officeHours[${index}].name`, s.name);
      formdata.append(`company.officeHours[${index}].startTime`, s.startTime);
      formdata.append(`company.officeHours[${index}].endTime`, s.endTime);
      formdata.append(`company.officeHours[${index}].isActive`, s.isActive);
    });

    agentRegisteration(formdata, handleRegistrationSuccess, !!userId);
  };

  useEffect(() => {
    getAllCountries();
    getAllLanguages();

    // eslint-disable-next-line
  }, []);

  const setSelectedCountryHandle = (option) => {
    setSelectedCountry(option);

    setValue("company.countryId", option.id);
    clearErrors("company.countryId");
  };

  const setSelectedLanguageHandle = (option) => {
    const ids = option?.map((o) => o.id);
    setSelectedLanguage(option);

    setValue("agent.languageSpokenIds", ids);
    clearErrors("agent.languageSpokenIds");
  };

  const setSelectedPreferredContactMethodHandle = (option) => {
    setSelectedPreferredContactMethod(option);

    setValue("agent.preferredContactMethodId", option.id);
    clearErrors("agent.preferredContactMethodId");
  };

  const setScheduleHandle = (value) => {
    setSchedule(value);
    setValue("officeHours", value);
  };

  const renderSlide = () => {
    switch (currentSlide) {
      case FORM_SLIDE.COMPANY_INFORAMTION:
        return (
          <CompanyInformationForm
            allCountries={allCountries}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountryHandle}
          />
        );
      case FORM_SLIDE.COMPANY_HOURS:
        return (
          <CompanyOfficeHoursForm
            schedule={schedule}
            setSchedule={setScheduleHandle}
            errors={errors}
            trigger={trigger}
          />
        );
      case FORM_SLIDE.AGENT_INFORMATION:
        return (
          <AgentInformationForm
            isDisabled={!!userId}
            allLanguages={allLanguages}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguageHandle}
            selectedPreferredContactMethod={selectedPreferredContactMethod}
            setSelectedPreferredContactMethod={
              setSelectedPreferredContactMethodHandle
            }
          />
        );
      case FORM_SLIDE.EMERGENCY_CONTACT:
        return <EmergencyContactForm />;
      default:
        return null;
    }
  };

  return (
    <Modal
      size="xl"
      centered
      show={show}
      onHide={onClose}
      backdrop="static"
      dialogClassName="agent-signup-modal"
      backdropClassName="auth-modal-backdrop custom-modal-backdrop"
    >
      <CustomSvg
        src={CloseAdminModalIcon}
        className="base-modal-close"
        onClick={onClose}
      />
      <Modal.Body>
        <div className="agent-signup-container">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              id="agent-signup-form"
            >
              {currentSlide <= totalFormSlides && (
                <ProgressBar now={getProgress()} />
              )}
              <div className="agent-signup-content">
                <SwitchTransition mode="out-in">
                  <CSSTransition
                    key={currentSlide}
                    addEndListener={(node, done) =>
                      node.addEventListener("transitionend", done, false)
                    }
                    classNames={
                      direction === SLIDE_DIRECTION.NEXT
                        ? "slide-next"
                        : "slide-prev"
                    }
                    timeout={300}
                  >
                    <div>{renderSlide()}</div>
                  </CSSTransition>
                </SwitchTransition>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center px-5">
          <div className="min-w-30">
            {currentSlide > 1 && currentSlide <= totalFormSlides && (
              <div
                className="slider-button rounded-circle prev text-center pointer d-flex justify-content-center align-items-center"
                onClick={handlePrevious}
              >
                <img src={ArrowLeftIcon} alt={SLIDE_DIRECTION.PREVIOUS} />
              </div>
            )}
          </div>
          <div>
            {currentSlide === totalFormSlides && (
              <button
                className="register-submit-button"
                type="submit"
                form="agent-signup-form"
                disabled={spinner(authConstants.AGENT_REGISTRATION) > -1}
              >
                {spinner(authConstants.AGENT_REGISTRATION) > -1
                  ? "Register..."
                  : "Register"}
              </button>
            )}
          </div>
          <div className="min-w-30">
            {currentSlide < totalFormSlides && (
              <div
                className="slider-button rounded-circle next text-center pointer d-flex justify-content-center align-items-center"
                onClick={handleNext}
              >
                <img src={ArrowRightIcon} alt={SLIDE_DIRECTION.NEXT} />
              </div>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  allCountries: state.countryReducer.allCountries,
  allLanguages: state.countryReducer.allLanguages,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  getAllLanguages: () => dispatch(countryAction.getAllLanguages()),
  getAllCountries: () => dispatch(countryAction.getAllCountries()),
  agentRegisteration: (data, successFunc, isRequiredReLogin) =>
    dispatch(
      authActions.agentRegisteration(data, successFunc, isRequiredReLogin)
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignupForm);
